import CTNSideMenu from '@ctn/ui/components/ctn-side-menu';
import CTNSideMenuButton from '@ctn/ui/components/ctn-side-menu/core/ctn-side-menu-button';
import CTNSideMenuGroup from '@ctn/ui/components/ctn-side-menu/core/ctn-side-menu-group';
import CTNSideMenuSeparator from '@ctn/ui/components/ctn-side-menu/core/ctn-side-menu-separator';
import { type Icon } from '@ctn/ui/types/generic';
import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDirtyForm } from '@/context/dirtyForm.context';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';

import { getUrlTramite } from '@/helpers/navigation';
import { type Permisos, type TramiteEstadoContratacion } from '@/interfaces/model/user.interface';
import type { CTNSideMenuRef } from '@ctn/ui/types/ctn-side-menu';

type MenuProps = {
	readonly isShowMenu: boolean;
	readonly isCollapseMenu: boolean;
	readonly onClose: () => void;
};

type MenuOption = {
	id: number;
	label: string;
	icon: Icon;
	url: string;
	disabled: boolean;
};

export default function Menu({ isShowMenu, isCollapseMenu, onClose }: MenuProps): React.ReactElement {
	const sideMenuRef = useRef<CTNSideMenuRef>(null);
	const navigate = useNavigate();
	const location = useLocation();
	const { getTramitesContratados, user } = useGlobalContext();
	const { checkDirtyForm } = useDirtyForm();
	const { setIsOpenHelpModal } = usePageContext();

	const getMenuOptions = (tramites: TramiteEstadoContratacion[], permisos: Permisos[]): MenuOption[] => {
		const tramiteIdPermitidos = permisos.map(({ tipoTramite }) => tipoTramite.id);
		return tramites.map((data) => {
			const isDisabled = !tramiteIdPermitidos.includes(data.tipoTramite.id);

			return {
				id: data.tipoTramite.id,
				label: data.tipoTramite.descripcion,
				icon: data.icono,
				url: getUrlTramite(data),
				disabled: isDisabled,
			};
		});
	};

	useEffect(() => {
		if (sideMenuRef?.current?.collapseMenu) {
			sideMenuRef?.current?.collapseMenu(isCollapseMenu);
		}
	}, [isCollapseMenu]);

	const disActiveMenuItem = (selected: string, route: string): boolean => selected.includes(route);

	return (
		<>
			{isShowMenu && (
				<CTNSideMenu
					ref={sideMenuRef}
					collapsable={false}
					className="[ o-layout__menu ] [ c-pnp-side-menu ]"
					isBreadcrumb={false}
					tabletTop={80}
					onClose={onClose}
				>
					<CTNSideMenuGroup>
						{user &&
							getMenuOptions(getTramitesContratados(), user.permisos).map(
								({ label, icon, url, disabled }) => (
									<CTNSideMenuButton
										key={label}
										label={label}
										disabled={disabled}
										icon={icon}
										active={disActiveMenuItem(location.pathname, url)}
										onClick={() => {
											checkDirtyForm({
												action() {
													navigate(url);
												},
											});
										}}
									/>
								),
							)}
					</CTNSideMenuGroup>

					<CTNSideMenuGroup type="secondary" className="u-mt-16">
						<CTNSideMenuSeparator />
						<CTNSideMenuButton
							className="c-pnp-side-menu__help"
							label="Ayuda y Manuales"
							icon="help"
							onClick={() => setIsOpenHelpModal(true)}
						/>
						<CTNSideMenuSeparator />
					</CTNSideMenuGroup>
				</CTNSideMenu>
			)}
		</>
	);
}
